// Those utils should NOT be used to configure/enable/disable product features
// based on the environment. You should use Gatekeeper for that.
// They are just here for rare configuration / networking edge-cases
import { z } from "zod";

import {
  CloudRegionKnownValue,
  NablaRegionKnownValue,
} from "generated/account";

import { getConfigForHost } from "./config";

const host = window.location.host;

export const isBehindIap =
  host.includes(".dev.") || host.includes("staging.") || host.includes("iap2.");

export const getConfig = getConfigForHost(host);
const protocol = window.location.protocol;

const regionUrlMap: Record<NablaRegionKnownValue, string> = {
  EU: "eu",
  US: "us",
};

// Used when parsing cloud regions from legacy URLs or tokens.
export const cloudRegionSchema = z
  .enum(["eu-west1", "us-central1"])
  .transform<CloudRegionKnownValue>(
    (label) =>
      ({
        "eu-west1": "EU_WEST1" as const,
        "us-central1": "US_CENTRAL1" as const,
      }[label]),
  );

// Used when parsing Nabla regions from URLs or tokens.
export const nablaRegionSchema = z
  .enum(["eu", "us"])
  .transform<NablaRegionKnownValue>(
    (label) =>
      ({
        eu: "EU" as const,
        us: "US" as const,
      }[label]),
  );

export const cloudRegionToNablaRegion = (
  cloudRegion: CloudRegionKnownValue,
): NablaRegionKnownValue =>
  ({
    EU_WEST1: "EU" as const,
    US_CENTRAL1: "US" as const,
  }[cloudRegion]);

const getPRNumber = () => Number(host.match(/pr-(\d+)\./u)?.[1]);

export const baseApiUrl = (region: NablaRegionKnownValue | undefined) => {
  const regionPathPart = region ? regionUrlMap[region] : "global";
  return `${window.location.origin}/api/${regionPathPart}`;
};

export const apiUrl = (region: NablaRegionKnownValue | undefined) =>
  `${baseApiUrl(region)}/v1`;

const maybeCommitHash = process.env.VITE_COMMIT_HASH;
export const commitHash = maybeCommitHash!;
const version = maybeCommitHash?.slice(0, 8) ?? "-1";
console.info(`Nabla Console (${version})`);

export const envColor = getConfig({
  dev: "bg-primary",
  pr: "bg-green",
  staging: "bg-coral",
  preprod: "bg-body",
  prod: "bg-primary-dark",
});

export const envText = getConfig({
  dev: "dev",
  pr: "pr",
  staging: "staging",
  preprod: "preprod",
  prod: undefined,
});

export const isPaletteEnabled = getConfig({
  dev: true,
  pr: true,
  staging: true,
  preprod: false,
  prod: false,
});

export const copilotChromeExtensionId = getConfig({
  dev: "lmccikojlfmnfnopkollijmbiikdbplc",
  pr: "oikkjlmegcabjgliedepdpmaniflgbce",
  staging: "ealkocnnjfflknpfhgfldhojmfbjjnge",
  preprod: "jijkjelijmamocloaanlejmagppampja",
  prod: "gdhbaoemgglcgmkidhnhcellgnehaeol",
});

export const defaultDomain = (() => host.split(":")[0])();

export const defaultOrigin = `${protocol}//${host}`;

export const getSafeArea = (position: "bottom" | "top") => {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(`--safe-area-${position}`)
    .slice(0, -2); // Remove "px"
  const number = Number(value);
  if (!number) return 0;
  // On iOS, value are a bit too big IMO (48 top, 34 bottom for iPhone X)
  // Need some investigation on android, so only truncate big values
  return number < 24 ? number : number - 10;
};

export const copilotWebappUrl = getConfig({
  dev: "http://localhost:3010",
  pr: `https://pr-${getPRNumber()}.copilot.pr.nabla.com`,
  staging: "https://copilot.staging.nabla.com",
  preprod: "https://copilot.preprod.nabla.com",
  prod: "https://copilot.nabla.com",
});

export const copilotMobileAppDeeplinkUrl = "nabla://copilot.nabla.com";
